// ============================================
// Top Navigation Bar
// Uses global layout classes from _layout.scss
// ============================================

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Popover from '@components/ui/Popover/Popover';
import Avatar from '@components/ui/Avatar/Avatar';
import ProfileImageEditor from '@components/shared/ProfileImageEditor';
import Select from '@workpay/ds-shared/components/ui/Select';
import { Flag } from '@workpay/ds-shared/components/ui/Flag';
import NotificationBell from '@components/shared/NotificationBell/NotificationBell';
import { useAuthStore } from '@/stores/authStore';
import { useLocalizationStore } from '@/stores/localizationStore';
import { logout, exitEntity } from '@/services/authService';
import { ClientSwitcherModal } from '@components/shared/ClientSwitcherModal/ClientSwitcherModal';
import { uploadFile } from '@/services/fileService';
import { getCurrentUser, setUserProfileImage, clearUserProfileImage } from '@/services/userService';
import { broadcastLogout } from '@/hooks/useAuthBroadcast';
import { getAvailableLanguages } from '@/services/localizationService';
import { useTranslation } from '@/hooks/useTranslation';
import ChevronDownSmallIcon from '@assets/icons/ChevronDownSmallIcon';
import LogoutIcon from '@assets/icons/LogoutIcon';
import MenuIcon from '@assets/icons/MenuIcon';
import SwitchClientIcon from '@assets/icons/SwitchClientIcon';

import type { SelectOption } from '@workpay/ds-shared/components/ui/Select';

// session.identity.roles is gone — derive the badge label from user.type.
// Only ADMIN vs default — matches prior behaviour (SUPER_ADMIN was a Keycloak
// role name, not a user.type, and is no longer exposed).
function getRoleLabel(
  userType: import('@/types/session').UserType | null | undefined,
  t: (key: string, fallback?: string) => string,
): string {
  if (userType === 'ADMIN') return t('K_ROLE_ADMIN', 'Admin');
  return t('K_ROLE_USER', 'User');
}

/** Map locale codes to display names using browser-native Intl API */
function getLanguageDisplayName(locale: string): string {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    const name = displayNames.of(locale);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : locale;
  } catch {
    return locale;
  }
}

export interface TopNavProps {
  collapsed?: boolean;
  /** Below the lg breakpoint, the sidebar collapses to a drawer — this fires
   *  the hamburger click that opens it. Hamburger button is hidden on desktop. */
  onMobileMenuClick?: () => void;
  /** Ref to the hamburger button — AppShell uses this to return focus to the
   *  trigger when the mobile drawer closes (a11y for keyboard / screen-reader). */
  mobileMenuTriggerRef?: React.Ref<HTMLButtonElement>;
}

export function TopNav({ collapsed, onMobileMenuClick, mobileMenuTriggerRef }: TopNavProps) {
  const { t } = useTranslation();
  const session = useAuthStore((s) => s.session);
  const clearSession = useAuthStore((s) => s.clearSession);
  const isEntitySession = useAuthStore((s) => s.isEntitySession);
  const language = useLocalizationStore((s) => s.language);
  const setLanguage = useLocalizationStore((s) => s.setLanguage);
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const organizationType = useAuthStore((s) => s.organizationType);
  const canSwitchEntity = hasPermission('ENTITY_SWITCH') && organizationType === 'WORKPAY' && !isEntitySession;

  // /users/me carries profileImagePath/profileImageUrl. Session payload doesn't,
  // so a separate query owns it and is the source of truth for "current avatar URL".
  const { data: me } = useQuery({
    queryKey: ['userMe'],
    queryFn: getCurrentUser,
    enabled: !!session,
    staleTime: 5 * 60 * 1000,
  });

  const platformUserId = me?.platformUserId ?? null;
  const avatarUrl = me?.profileImageUrl ?? null;

  // Self-edit save: upload bytes → backend returns `bucketPath` → link as `path`
  // on users.profile_image_path. file-management-service has no callback into
  // platform-service; the FE owns both legs. Invalidate ['userMe'] so the navbar
  // avatar re-renders with the new profileImageUrl.
  const handleProfileImageSave = useCallback(
    async (blob: Blob) => {
      if (!platformUserId) {
        throw new Error('User identity not loaded yet — try again in a moment.');
      }
      const file = new File([blob], `profile-${platformUserId}-${Date.now()}.jpg`, {
        type: blob.type || 'image/jpeg',
      });
      const uploadStarted = performance.now();
      const uploaded = await uploadFile(file, {
        workflow: 'PROFILE_IMAGE',
        objectType: 'USER',
        objectId: platformUserId,
      });
      if (!uploaded.bucketPath) {
        throw new Error(
          'Upload response missing bucketPath — file-management-service may not be running ' +
            'a recent enough build, or the PROFILE_IMAGE workflow is not configured to use the public bucket.',
        );
      }
      const linkStarted = performance.now();
      const updated = await setUserProfileImage(platformUserId, uploaded.bucketPath);
      console.log('[ProfileImage upload] self', {
        platformUserId,
        uploadedFileId: uploaded.id,
        fileName: uploaded.fileName,
        contentType: uploaded.contentType,
        sizeBytes: uploaded.size,
        sizeKb: (uploaded.size / 1024).toFixed(1),
        bucketPath: uploaded.bucketPath,
        uploadMs: Math.round(linkStarted - uploadStarted),
        linkMs: Math.round(performance.now() - linkStarted),
        finalProfileImagePath: updated.profileImagePath,
        finalProfileImageUrl: updated.profileImageUrl,
      });
      void queryClient.invalidateQueries({ queryKey: ['userMe'] });
    },
    [platformUserId, queryClient, t],
  );

  const handleProfileImageRemove = useCallback(async () => {
    if (!platformUserId) return;
    await clearUserProfileImage(platformUserId);
    void queryClient.invalidateQueries({ queryKey: ['userMe'] });
  }, [platformUserId, queryClient]);

  const canEditOwnAvatar = hasPermission('USERS_UPDATE') && !!platformUserId;

  const { data: languages } = useQuery({
    queryKey: ['localization', 'languages'],
    queryFn: getAvailableLanguages,
    staleTime: 10 * 60 * 1000,
  });

  const languageOptions: SelectOption[] = (languages ?? ['en-US']).map((lang) => ({
    label: getLanguageDisplayName(lang),
    value: lang,
    icon: <Flag locale={lang} title={getLanguageDisplayName(lang)} />,
  }));

  const firstName = session?.user.firstName ?? '';
  const lastName = session?.user.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const email = session?.user.email ?? '';
  const roleLabel = session ? getRoleLabel(session.user.type, t) : '';

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setDropdownOpen(false);
    try {
      await logout();
    } finally {
      // Order matters. clearSession() flips authStore.session to null but does
      // not touch the TanStack Query cache, so __root.tsx's effect re-hydrates
      // from cached data on the very next render (`if (data && !session)` is
      // true again) — racing window.location.href. Removing the cached session
      // before clearing the store closes the race.
      queryClient.removeQueries({ queryKey: ['session'] });
      // Wipe persisted column-pref cache so user A's layouts don't leak into
      // user B in the same tab. The BE store itself is per-(orgId, userId)
      // — this is purely a client-cache hygiene step.
      queryClient.removeQueries({ queryKey: ['userColumnConfig'] });
      broadcastLogout();
      clearSession();
      const redirectPath = window.location.pathname + window.location.search;
      const loginUrl =
        redirectPath && redirectPath !== '/' && redirectPath !== '/login'
          ? `/login?redirect_to=${encodeURIComponent(redirectPath)}`
          : '/login';
      window.location.href = loginUrl;
    }
  }, [clearSession, queryClient, isLoggingOut]);

  const dropdownContent = (
    <div className="profile-dropdown">
      <div className="profile-dropdown__header">
        <ProfileImageEditor
          displayName={fullName || t('K_FALLBACK_USER', 'User')}
          size="md"
          currentImageUrl={avatarUrl}
          canEdit={canEditOwnAvatar}
          onSave={handleProfileImageSave}
          onRemove={handleProfileImageRemove}
          captureMode="user"
        />
        <div className="profile-dropdown__user-info">
          <span className="profile-dropdown__name">{fullName || t('K_FALLBACK_USER', 'User')}</span>
          <span className="profile-dropdown__role">
            {email}
            {roleLabel && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: '#EBF5E6',
                  color: '#62A446',
                  marginInlineStart: '4px',
                }}
              >
                {roleLabel}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="profile-dropdown__items">
        {canSwitchEntity && (
          <button
            type="button"
            className="profile-dropdown__item"
            onClick={() => { setDropdownOpen(false); setSwitcherOpen(true); }}
          >
            <SwitchClientIcon />
            {t('K_BUTTON_SWITCH_TO_CLIENT', 'Switch to Client')}
          </button>
        )}
        {isEntitySession ? (
          <button
            type="button"
            className="profile-dropdown__item"
            onClick={() => { void exitEntity().then((url) => { window.location.href = url || '/'; }); }}
          >
            <LogoutIcon />
            {t('K_BUTTON_EXIT_TO_MASTER', 'Exit to Master')}
          </button>
        ) : (
          <button
            type="button"
            className="profile-dropdown__item"
            onClick={() => void handleLogout()}
            disabled={isLoggingOut}
            aria-busy={isLoggingOut}
          >
            <LogoutIcon />
            {isLoggingOut ? t('K_LOGGING_OUT', 'Logging out...') : t('K_LOGOUT', 'Logout')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
    <header className={`topnav${collapsed ? ' topnav--collapsed' : ''}`}>
      <div className="topnav__left">
        {onMobileMenuClick && (
          <button
            type="button"
            className="topnav__menu-toggle"
            onClick={onMobileMenuClick}
            ref={mobileMenuTriggerRef}
            aria-label={t('K_ARIA_OPEN_NAV_MENU', 'Open navigation menu')}
          >
            <MenuIcon />
          </button>
        )}
      </div>

      <div className="topnav__right">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginInlineEnd: '16px' }}>
          <Select
            options={languageOptions}
            value={language}
            onChange={setLanguage}
            size="sm"
          />
        </div>
        <NotificationBell />
        <Popover
          content={dropdownContent}
          placement="bottom-end"
          trigger="click"
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
          className="profile-dropdown-popover"
        >
          <div className="topnav__profile">
            <Avatar name={fullName} size="sm" {...(avatarUrl ? { src: avatarUrl } : {})} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span
                style={{
                  fontFamily: "'Moderat', sans-serif",
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#253545',
                  maxWidth: '160px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fullName || t('K_FALLBACK_USER', 'User')}
              </span>
              <span
                style={{
                  fontFamily: "'Moderat', sans-serif",
                  fontSize: '14px',
                  color: '#62A446',
                }}
              >
                {roleLabel || t('K_ROLE_USER', 'User')}
              </span>
            </div>
            <ChevronDownSmallIcon />
          </div>
        </Popover>
      </div>
    </header>
    <ClientSwitcherModal key={String(switcherOpen)} isOpen={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </>
  );
}
